import React from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Truck, UserCheck } from "lucide-react";
import { adminApi } from "../../lib/api";

export default function DistributionSubcontractorPanel() {
  const [subcontractors, setSubcontractors] = React.useState([]);
  const [routes, setRoutes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [assignSc, setAssignSc] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [scRes, routesRes] = await Promise.all([
        adminApi().get("/admin/distribution/subcontractors/ready"),
        adminApi().get("/admin/distribution/routes", {
          params: { hub_id: "roskilde", limit: 30 },
        }),
      ]);
      setSubcontractors(scRes.data.items || []);
      setRoutes(routesRes.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente vognmand-data");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const assign = async (routeId) => {
    const scId = assignSc[routeId];
    if (!scId) {
      toast.error("Vælg vognmand først");
      return;
    }
    setBusy(routeId);
    try {
      const { data } = await adminApi().post(
        `/admin/distribution/routes/${routeId}/assign-subcontractor`,
        { subcontractor_id: scId }
      );
      toast.success(data.message || "Tildelt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke tildele rute");
    } finally {
      setBusy("");
    }
  };

  const ready = subcontractors.filter((s) => s.ready_for_routes);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-brand-text2 py-8">
        <Loader2 className="animate-spin" size={18} /> Indlæser underleverandører...
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="distribution-subcontractor-panel">
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center gap-3">
          <Truck size={20} className="text-brand-red" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">
              Underleverandør (UE)
            </div>
            <div className="font-display text-lg text-brand-dark">
              Vognmand · ruter Roskilde
            </div>
          </div>
          <button type="button" onClick={load} className="px-3 py-1.5 text-xs border border-brand flex items-center gap-1">
            <RefreshCw size={12} /> Opdater
          </button>
        </div>
        <p className="text-xs text-brand-text2 mt-3 leading-relaxed">
          Vognmand skal have <strong>godkendt onboarding</strong> + mindst én godkendt chauffør.
          Tildelte ruter vises i <code>/vognmand</code> under Egne ruter.
        </p>
      </div>

      <section className="bg-white border border-brand overflow-hidden">
        <div className="p-4 border-b border-brand flex items-center gap-2">
          <UserCheck size={16} className="text-brand-red" />
          <h3 className="font-semibold text-brand-dark">Vognmænd</h3>
        </div>
        {subcontractors.length === 0 ? (
          <div className="p-6 text-sm text-brand-text2 text-center">
            Ingen vognmænd endnu — opret under Admin → Vognmænd.
          </div>
        ) : (
          <ul className="divide-y divide-brand text-sm">
            {subcontractors.map((s) => (
              <li key={s.id} className="p-3 flex flex-wrap justify-between gap-2">
                <div>
                  <strong>{s.company_name}</strong>
                  <div className="text-xs text-brand-text2">
                    Onboarding: {s.onboarding_status || "—"} · {s.approved_drivers} chauffør(er)
                  </div>
                  {s.block_reason && !s.ready_for_routes && (
                    <div className="text-xs text-amber-700 mt-1">{s.block_reason}</div>
                  )}
                </div>
                <span
                  className={`text-xs px-2 py-0.5 h-fit ${
                    s.ready_for_routes ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {s.ready_for_routes ? "Klar til ruter" : "Ikke klar"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white border border-brand overflow-hidden">
        <div className="p-4 border-b border-brand">
          <h3 className="font-semibold text-brand-dark">Ruter (Roskilde)</h3>
        </div>
        {routes.length === 0 ? (
          <div className="p-6 text-sm text-brand-text2 text-center">
            Ingen ruter endnu — kør plan under HUBs & plan (vælg vognmand ved planlægning).
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brand-bg text-xs uppercase text-brand-text2">
              <tr>
                <th className="text-left p-3">Rute</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Vognmand</th>
                <th className="text-left p-3">Tildel</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={r.id} className="border-t border-brand">
                  <td className="p-3 font-mono text-xs">{r.route_number}</td>
                  <td className="p-3">{r.route_type}</td>
                  <td className="p-3">{r.status}</td>
                  <td className="p-3">{r.subcontractor_name || "—"}</td>
                  <td className="p-3">
                    {r.subcontractor_id ? (
                      <span className="text-emerald-600 text-xs">Tildelt</span>
                    ) : (
                      <div className="flex gap-2 items-center flex-wrap">
                        <select
                          className="input-flat py-1 text-xs"
                          value={assignSc[r.id] || ""}
                          onChange={(e) => setAssignSc((m) => ({ ...m, [r.id]: e.target.value }))}
                        >
                          <option value="">Vælg UE</option>
                          {ready.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.company_name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={busy === r.id || ready.length === 0}
                          onClick={() => assign(r.id)}
                          className="px-2 py-1 text-xs bg-brand-dark text-white"
                        >
                          {busy === r.id ? "…" : "Tildel"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
